// MoveTargetRings — renders semi-transparent rings on adjacent hexes when
// a unit is selected. Green = passable and unoccupied; red = impassable or
// occupied. Clicking a green-ring hex fires a move command (handled in App).

import type { SceneNode } from '@aod/protocol';
import { NAVAL_KINDS } from '@aod/ui/prefabs';
import { useMemo } from 'react';
import * as THREE from 'three';
import { axialToWorld } from './HexGrid.js';
import { buildBiomeMap, tileElevationAt, waterSurfaceY } from './elevation.js';

interface MoveTarget {
  readonly q: number;
  readonly r: number;
  readonly valid: boolean;
  readonly reason?: 'off_map' | 'impassable' | 'occupied';
}

interface MoveTargetRingsProps {
  readonly unit: SceneNode;
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly cities: ReadonlyArray<SceneNode>;
  readonly units: ReadonlyArray<SceneNode>;
  readonly onMoveClick?: (q: number, r: number) => void;
}

/** Axial direction vectors matching the sim's AXIAL_DIRS. */
const AXIAL_DIRS = [
  { q: 1, r: 0 }, // E
  { q: 1, r: -1 }, // NE
  { q: 0, r: -1 }, // NW
  { q: -1, r: 0 }, // W
  { q: -1, r: 1 }, // SW
  { q: 0, r: 1 }, // SE
];

export function MoveTargetRings({ unit, tiles, cities, units, onMoveClick }: MoveTargetRingsProps) {
  const targets = useMemo(
    () => computeMoveTargets(unit, tiles, cities, units),
    [unit, tiles, cities, units],
  );
  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);
  const tileMap = useMemo(() => {
    const m = new Map<string, SceneNode>();
    for (const t of tiles) {
      const tq = t.props?.q as number | undefined;
      const tr = t.props?.r as number | undefined;
      if (tq !== undefined && tr !== undefined) m.set(`${tq},${tr}`, t);
    }
    return m;
  }, [tiles]);
  const validGeometry = useMemo(() => new THREE.RingGeometry(0.2, 0.35, 32), []);
  const invalidGeometry = useMemo(() => new THREE.RingGeometry(0.2, 0.35, 32), []);

  if (targets.length === 0) return null;

  return (
    <>
      {targets.map((t) => {
        const pos = axialToWorld(t.q, t.r);
        // A ship's rings sit on the water surface, not on the seabed.
        const biome = tileMap.get(`${t.q},${t.r}`)?.props?.biome as string | undefined;
        const elev =
          biome === 'ocean' || biome === 'coast' || biome === 'river'
            ? waterSurfaceY(t.q, t.r, biomeMap) + 0.02
            : tileElevationAt(t.q, t.r, biomeMap);
        if (!t.valid) {
          return (
            <mesh
              key={`mt-${t.q}-${t.r}`}
              rotation={[-Math.PI / 2, 0, 0]}
              position={[pos[0], elev + 0.015, pos[2]]}
              geometry={invalidGeometry}
            >
              <meshBasicMaterial
                color="#ff4444"
                transparent
                opacity={0.35}
                side={THREE.DoubleSide}
              />
            </mesh>
          );
        }
        return (
          <mesh
            key={`mt-${t.q}-${t.r}`}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[pos[0], elev + 0.015, pos[2]]}
            geometry={validGeometry}
            onClick={(e) => {
              e.stopPropagation();
              onMoveClick?.(t.q, t.r);
            }}
          >
            <meshBasicMaterial color="#44ff44" transparent opacity={0.45} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
    </>
  );
}

export function computeMoveTargets(
  unit: SceneNode,
  tiles: ReadonlyArray<SceneNode>,
  cities: ReadonlyArray<SceneNode>,
  units: ReadonlyArray<SceneNode>,
): MoveTarget[] {
  const q = unit.props?.q as number | undefined;
  const r = unit.props?.r as number | undefined;
  if (q === undefined || r === undefined) return [];
  const ownerId = unit.props?.ownerId as number | undefined;

  const tileMap = new Map<string, SceneNode>();
  for (const t of tiles) {
    const tq = t.props?.q as number | undefined;
    const tr = t.props?.r as number | undefined;
    if (tq !== undefined && tr !== undefined) {
      tileMap.set(`${tq},${tr}`, t);
    }
  }

  const occupantMap = new Map<string, { ownerId: number; kind: string }>();
  for (const c of cities) {
    const cq = c.props?.q as number | undefined;
    const cr = c.props?.r as number | undefined;
    const co = c.props?.ownerId as number | undefined;
    if (cq !== undefined && cr !== undefined && co !== undefined) {
      occupantMap.set(`${cq},${cr}`, { ownerId: co, kind: 'city' });
    }
  }
  for (const u of units) {
    const uq = u.props?.q as number | undefined;
    const ur = u.props?.r as number | undefined;
    const uo = u.props?.ownerId as number | undefined;
    if (uq !== undefined && ur !== undefined && uo !== undefined) {
      // Don't mark the selected unit itself as an occupant.
      if (u.id === unit.id) continue;
      occupantMap.set(`${uq},${ur}`, { ownerId: uo, kind: 'unit' });
    }
  }

  // A ship is not limited to the six neighbouring hexes and ocean is its road:
  // it gets a breadth-first reach over water, out to its movement allowance.
  const kind = unit.props?.kind as string | undefined;
  if (kind && NAVAL_KINDS.has(kind)) {
    const max = (unit.props?.maxMovement as number | undefined) ?? 3;
    return navalTargets(q, r, max, tileMap, occupantMap, ownerId);
  }

  const impassableBiomes = new Set(['ocean', 'mountain']);
  const results: MoveTarget[] = [];

  for (const d of AXIAL_DIRS) {
    const nq = q + d.q;
    const nr = r + d.r;
    const key = `${nq},${nr}`;

    const tile = tileMap.get(key);
    if (!tile) {
      results.push({ q: nq, r: nr, valid: false, reason: 'off_map' });
      continue;
    }

    const biome = tile.props?.biome as string | undefined;
    if (biome && impassableBiomes.has(biome)) {
      results.push({ q: nq, r: nr, valid: false, reason: 'impassable' });
      continue;
    }

    const occ = occupantMap.get(key);
    if (occ) {
      if (occ.kind === 'city' || occ.ownerId === ownerId) {
        results.push({ q: nq, r: nr, valid: false, reason: 'occupied' });
        continue;
      }
      // Enemy unit → valid combat target (green ring).
    }

    results.push({ q: nq, r: nr, valid: true });
  }

  return results;
}

/**
 * Water hexes a ship can reach this turn (BFS over Ocean/Coast). Land hexes are
 * reported as impassable so the player can see why the sea stops where it does.
 */
function navalTargets(
  q: number,
  r: number,
  max: number,
  tileMap: ReadonlyMap<string, SceneNode>,
  occupantMap: ReadonlyMap<string, { ownerId: number; kind: string }>,
  ownerId: number | undefined,
): MoveTarget[] {
  const isWater = (t: SceneNode | undefined) => {
    const b = t?.props?.biome as string | undefined;
    return b === 'ocean' || b === 'coast';
  };
  const seen = new Set<string>([`${q},${r}`]);
  const results: MoveTarget[] = [];
  let frontier: Array<{ q: number; r: number }> = [{ q, r }];
  for (let step = 0; step < max && frontier.length > 0; step++) {
    const next: Array<{ q: number; r: number }> = [];
    for (const cur of frontier) {
      for (const d of AXIAL_DIRS) {
        const nq = cur.q + d.q;
        const nr = cur.r + d.r;
        const key = `${nq},${nr}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const tile = tileMap.get(key);
        if (!tile) continue; // off the board: nothing to draw
        if (!isWater(tile)) {
          results.push({ q: nq, r: nr, valid: false, reason: 'impassable' });
          continue;
        }
        const occ = occupantMap.get(key);
        if (occ && (occ.kind === 'city' || occ.ownerId === ownerId)) {
          results.push({ q: nq, r: nr, valid: false, reason: 'occupied' });
          continue;
        }
        results.push({ q: nq, r: nr, valid: true });
        next.push({ q: nq, r: nr });
      }
    }
    frontier = next;
  }
  return results;
}
